/**
 * 支付渠道字段 schema（管理端）。
 *
 * 按渠道类型定义「非敏感展示配置（config）」与「加密只写密钥（secret）」两组字段：
 * - config：明文存于 PaymentChannel.config，可回显、可编辑（appId、公钥、商户号、收款账户等）。
 * - secret：AES-256-GCM 加密存于 secrets_enc，**只写不回显**；编辑时留空表示沿用原值（后端按字段合并保留）。
 *
 * 字段键名严格对齐后端 provider 读取的 config/secrets 键（见 alipay/wechat/bank-transfer.provider.ts），
 * 避免表单存的键与 provider 取的键不一致导致"配了却不生效"。
 *
 * 同理，CHANNEL_SPECS 的键（= 落库的 channel_type）也是契约的一部分，必须与后端
 * PaymentRegistry 注册的 provider 键逐字一致，否则渠道能建能启用，用户一下单就报
 * 「不支持的支付渠道类型」。
 */

export type ChannelType = 'alipay' | 'wechat' | 'bank_transfer';

export interface ChannelFieldSpec {
  /** 存储键名（对齐 provider 读取的 config.xxx / secrets.xxx） */
  key: string;
  label: string;
  /** config=非敏感展示配置；secret=加密只写密钥 */
  store: 'config' | 'secret';
  /** 输入控件：短文本 / 多行（PEM、说明）/ 密码（短密钥，输入即掩码） */
  input: 'text' | 'textarea' | 'password';
  placeholder?: string;
  tip?: string;
  required?: boolean;
}

export interface ChannelTypeSpec {
  type: ChannelType;
  label: string;
  color: string;
  description: string;
  fields: ChannelFieldSpec[];
}

/** 渠道类型元信息 + 字段定义（顺序即表单渲染顺序） */
export const CHANNEL_SPECS: Record<ChannelType, ChannelTypeSpec> = {
  alipay: {
    type: 'alipay',
    label: '支付宝',
    color: '#1677ff',
    description: '电脑网站 / 手机网站支付，RSA2 验签。需填写应用 AppId、支付宝公钥与应用私钥。',
    fields: [
      { key: 'appId', label: '应用 AppId', store: 'config', input: 'text', required: true, placeholder: '如 2021000000000000', tip: '支付宝开放平台应用 AppId（非敏感）' },
      { key: 'alipayPublicKey', label: '支付宝公钥', store: 'config', input: 'textarea', placeholder: '粘贴支付宝公钥（用于异步通知验签）', tip: '公钥可明文存储，用于校验支付宝回调签名' },
      { key: 'gateway', label: '网关地址', store: 'config', input: 'text', placeholder: '留空使用默认正式网关', tip: '沙箱联调可填沙箱网关，留空为 https://openapi.alipay.com/gateway.do' },
      { key: 'privateKey', label: '应用私钥', store: 'secret', input: 'textarea', required: true, placeholder: '粘贴应用私钥（PKCS8，加密存储）', tip: '敏感密钥，AES-256-GCM 加密存储，只写不回显；编辑时留空表示保持不变' },
    ],
  },
  wechat: {
    type: 'wechat',
    label: '微信支付',
    color: '#07c160',
    description:
      'Native 扫码支付（APIv3）。需填写商户号、AppId、证书序列号，以及商户私钥与 APIv3 密钥；移动端 App 支付另填下面的「App 支付 AppID」。',
    fields: [
      { key: 'mchid', label: '商户号 (mchid)', store: 'config', input: 'text', required: true, placeholder: '如 1900000000' },
      { key: 'appId', label: 'AppId', store: 'config', input: 'text', required: true, placeholder: '公众号 / 开放平台 AppId' },
      { key: 'appAppId', label: 'App 支付 AppID', store: 'config', input: 'text', placeholder: '微信开放平台移动应用 AppID（仅 App 支付需要）', tip: '与上面的 AppId 不是同一个：这个是移动端 App 在开放平台单独注册的 AppID。手机端下单走 App 支付时后端只读它，留空则回落到上面的 AppId。' },
      { key: 'serialNo', label: '商户证书序列号', store: 'config', input: 'text', placeholder: 'apiclient_cert.pem 证书序列号', tip: '用于 APIv3 请求头签名标识（非敏感）' },
      { key: 'gateway', label: '网关地址', store: 'config', input: 'text', placeholder: '留空使用默认 https://api.mch.weixin.qq.com' },
      { key: 'privateKey', label: '商户 API 私钥', store: 'secret', input: 'textarea', required: true, placeholder: '粘贴 apiclient_key.pem 内容（加密存储）', tip: '敏感密钥，加密存储、只写不回显；留空保持不变' },
      { key: 'apiV3Key', label: 'APIv3 密钥', store: 'secret', input: 'password', required: true, placeholder: '32 位 APIv3 密钥', tip: '用于回调解密，加密存储、只写不回显；留空保持不变' },
      { key: 'platformCert', label: '平台证书', store: 'secret', input: 'textarea', placeholder: '微信支付平台证书（验签用，可选）', tip: '用于校验回调签名，可后续补充；留空保持不变' },
      { key: 'platformSerial', label: '平台证书序列号', store: 'secret', input: 'password', placeholder: '平台证书序列号（可选）', tip: '与平台证书配对校验；留空保持不变' },
    ],
  },
  bank_transfer: {
    type: 'bank_transfer',
    label: '银行转账',
    color: '#f59e0b',
    description: '离线对公 / 对私转账。此处维护的收款账户信息会展示给付款用户，转账到账后由管理端人工确认发放会员。',
    fields: [
      { key: 'bankName', label: '开户银行', store: 'config', input: 'text', required: true, placeholder: '如 招商银行' },
      { key: 'accountName', label: '收款户名', store: 'config', input: 'text', required: true, placeholder: '如 XX科技有限公司' },
      { key: 'accountNo', label: '收款账号', store: 'config', input: 'text', required: true, placeholder: '银行账号 / 卡号' },
      { key: 'branch', label: '支行 / 网点', store: 'config', input: 'text', placeholder: '如 上海分行XX支行（可选）' },
      { key: 'qrCodeUrl', label: '收款二维码 URL', store: 'config', input: 'text', placeholder: '收款码图片地址（可选）' },
      { key: 'instructions', label: '转账说明', store: 'config', input: 'textarea', placeholder: '如：请备注订单号，转账后上传凭证等待审核（可选）' },
    ],
  },
};

/** 渠道类型下拉选项 */
export const CHANNEL_TYPE_OPTIONS = (Object.keys(CHANNEL_SPECS) as ChannelType[]).map((t) => ({
  value: t,
  label: CHANNEL_SPECS[t].label,
}));

/** 类型中文名（含未知类型回落） */
export function channelTypeLabel(type: string): string {
  return CHANNEL_SPECS[type as ChannelType]?.label ?? type;
}

/** 类型主题色（含未知类型回落灰色） */
export function channelTypeColor(type: string): string {
  return CHANNEL_SPECS[type as ChannelType]?.color ?? '#8a9099';
}
